import {
  countries,
  CountryCode,
  isCountryCode,
} from "@redotech/locale/countries";
import { USAProvinceCode, usaProvinces } from "@redotech/locale/provinces";
import { useHandler } from "@redotech/react-util/hook";
import EditPencilIcon from "@redotech/redo-icons/edit-01.svg";
import {
  rcoDeliveryLocationSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { SegmentListSingleItemMembershipOperator } from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { OperatorSelector } from "@redotech/redo-web/conversion/operator-selector";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { arraysEqualIgnoreOrder } from "@redotech/util/equal";
import { memo, useMemo, useState } from "react";
import { PartialWithRequiredType } from "../..";
import { CountrySelectionModal } from "./country-selection-modal";
import { UsProvinceSelectionModal } from "./us-province-selection-modal";

export const DeliveryLocationInspectorContent = memo(
  function DeliveryLocationInspectorContent({
    step,
  }: {
    step: RcoSegmentStep;
  }) {
    const segment = useMemo(
      () => rcoDeliveryLocationSegmentSchema.parse(step.segment),
      [step],
    );

    const isUsExclusivelySelected = useMemo(() => {
      return isUnitedStatesExclusivelySelected(
        segment.locationCountry.countries,
        segment.locationCountry.operator,
      );
    }, [segment.locationCountry]);

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            Delivery location is{" "}
            {locationOperatorToText[segment.locationCountry.operator]} country
          </Text>
        </Flex>
        <Flex dir="row" gap="md" wrap="wrap">
          {segment.locationCountry.countries.map((country) => {
            const countryName = countries.find((c) => c.code === country)?.name;
            if (!countryName) {
              return null;
            }

            return (
              <RedoBadge
                color="gray"
                key={country}
                size="sm"
                text={countryName}
              />
            );
          })}
        </Flex>
        {isUsExclusivelySelected && segment.locationProvince && (
          <>
            <Text fontSize="xs" fontWeight="medium" textColor="primary">
              and {locationOperatorToText[segment.locationProvince.operator]}{" "}
              province
            </Text>
            <Flex dir="row" gap="md" wrap="wrap">
              {segment.locationProvince.provinces.map((province) => {
                const provinceName = usaProvinces.find(
                  (p) => p.code === province,
                )?.name;
                if (!provinceName) {
                  return null;
                }

                return (
                  <RedoBadge
                    color="gray"
                    key={province}
                    size="sm"
                    text={provinceName}
                  />
                );
              })}
            </Flex>
          </>
        )}
      </>
    );
  },
);

export const EditableDeliveryLocationInspectorContent = memo(
  function EditableDeliveryLocationInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const segment = useMemo(
      () => rcoDeliveryLocationSegmentSchema.parse(step.segment),
      [step],
    );

    const [isCountryModalOpen, setIsCountryModalOpen] = useState(false);
    const [isProvinceModalOpen, setIsProvinceModalOpen] = useState(false);
    const [showProvinceFilter, setShowProvinceFilter] = useState(
      !!segment.locationProvince,
    );

    const isUsExclusivelySelected = useMemo(() => {
      return isUnitedStatesExclusivelySelected(
        segment.locationCountry.countries,
        segment.locationCountry.operator,
      );
    }, [segment.locationCountry]);

    const handleCountryOperatorSelected = useHandler(
      (operator: SegmentListSingleItemMembershipOperator) => {
        const updatedSegment = {
          ...segment,
          locationCountry: { ...segment.locationCountry, operator },
        };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleCountriesSelected = useHandler((countries: CountryCode[]) => {
      const hasChanged = !arraysEqualIgnoreOrder(
        segment.locationCountry.countries,
        countries,
      );
      if (!hasChanged) {
        return;
      }

      const hasUsExclusive = isUnitedStatesExclusivelySelected(
        countries,
        segment.locationCountry.operator,
      );

      const updatedSegment = (() => {
        if (hasUsExclusive && showProvinceFilter) {
          return {
            ...segment,
            locationCountry: { ...segment.locationCountry, countries },
            locationProvince: {
              ...segment.locationProvince,
              operator: SegmentListSingleItemMembershipOperator.IN,
              provinces:
                segment.locationProvince?.provinces ||
                usaProvinces.map((p) => p.code),
            },
          };
        } else {
          const { locationProvince, ...segmentWithoutProvince } = segment;
          return {
            ...segmentWithoutProvince,
            locationCountry: { ...segment.locationCountry, countries },
          };
        }
      })();

      setStep({ ...step, segment: updatedSegment });
      setIsCountryModalOpen(false);
    });

    const handleProvinceOperatorSelected = useHandler(
      (operator: SegmentListSingleItemMembershipOperator) => {
        const existingLocationProvince = segment.locationProvince || {
          provinces: [],
          operator: SegmentListSingleItemMembershipOperator.IN,
        };

        const updatedSegment = {
          ...segment,
          locationProvince: { ...existingLocationProvince, operator },
        };

        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleProvincesSelected = useHandler(
      (provinces: USAProvinceCode[]) => {
        if (provinces.length === 0) {
          return;
        }

        const existingLocationProvince = segment.locationProvince || {
          provinces: [],
          operator: SegmentListSingleItemMembershipOperator.IN,
        };

        const updatedSegment = {
          ...segment,
          locationProvince: { ...existingLocationProvince, provinces },
        };

        setStep({ ...step, segment: updatedSegment });
        setIsProvinceModalOpen(false);
      },
    );

    const handleToggleProvinceFilter = useHandler((value: boolean) => {
      setShowProvinceFilter(value);

      if (value) {
        const locationProvince = segment.locationProvince || {
          provinces: [],
          operator: SegmentListSingleItemMembershipOperator.IN,
        };

        const updatedSegment = { ...segment, locationProvince };
        setStep({ ...step, segment: updatedSegment });
      } else {
        // Destructure to omit the locationProvince key
        const { locationProvince, ...segmentWithoutProvince } = segment;
        setStep({ ...step, segment: segmentWithoutProvince });
      }
    });

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            Delivery location is
          </Text>
        </Flex>
        <Flex>
          <OperatorSelector<SegmentListSingleItemMembershipOperator>
            availableOptions={Object.values(
              SegmentListSingleItemMembershipOperator,
            )}
            onOperatorSelected={handleCountryOperatorSelected}
            operatorToText={locationOperatorToText}
            selectedOperator={segment.locationCountry.operator}
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          country
        </Text>
        <Flex dir="row" gap="md" wrap="wrap">
          {segment.locationCountry.countries.length > 0 ? (
            segment.locationCountry.countries.map((country) => {
              const countryName = countries.find(
                (c) => c.code === country,
              )?.name;
              if (!countryName) {
                return null;
              }

              return (
                <RedoBadge
                  color="gray"
                  key={country}
                  size="sm"
                  text={countryName}
                />
              );
            })
          ) : (
            <Text fontSize="xs" textColor="tertiary">
              No countries selected
            </Text>
          )}
        </Flex>
        <Flex justify="flex-end" w="full">
          <RedoButton
            hierarchy="secondary"
            IconLeading={EditPencilIcon}
            onClick={() => setIsCountryModalOpen(true)}
            size="xs"
            text="Edit list"
          />
        </Flex>
        {isUsExclusivelySelected && (
          <>
            <RedoToggle
              label="Filter by state/province"
              setValue={handleToggleProvinceFilter}
              size="xs"
              value={showProvinceFilter}
            />
            {showProvinceFilter && (
              <>
                <Text fontSize="xs" fontWeight="medium" textColor="primary">
                  and is
                </Text>
                <Flex>
                  <OperatorSelector<SegmentListSingleItemMembershipOperator>
                    availableOptions={Object.values(
                      SegmentListSingleItemMembershipOperator,
                    )}
                    onOperatorSelected={handleProvinceOperatorSelected}
                    operatorToText={locationOperatorToText}
                    selectedOperator={
                      segment.locationProvince?.operator ||
                      SegmentListSingleItemMembershipOperator.IN
                    }
                  />
                </Flex>
                <Text fontSize="xs" fontWeight="medium" textColor="primary">
                  province
                </Text>
                <Flex dir="row" gap="md" wrap="wrap">
                  {segment.locationProvince?.provinces &&
                  segment.locationProvince.provinces.length > 0 ? (
                    segment.locationProvince.provinces.map((province) => {
                      const provinceName = usaProvinces.find(
                        (p) => p.code === province,
                      )?.name;
                      if (!provinceName) {
                        return null;
                      }

                      return (
                        <RedoBadge
                          color="gray"
                          key={province}
                          size="sm"
                          text={provinceName}
                        />
                      );
                    })
                  ) : (
                    <Text fontSize="xs" textColor="tertiary">
                      No provinces selected
                    </Text>
                  )}
                </Flex>
                <Flex justify="flex-end" w="full">
                  <RedoButton
                    hierarchy="secondary"
                    IconLeading={EditPencilIcon}
                    onClick={() => setIsProvinceModalOpen(true)}
                    size="xs"
                    text="Edit list"
                  />
                </Flex>
              </>
            )}
          </>
        )}
        {isCountryModalOpen && (
          <CountrySelectionModal
            onClose={() => setIsCountryModalOpen(false)}
            onCountriesSelected={handleCountriesSelected}
            selectedCountries={segment.locationCountry.countries.filter(
              isCountryCode,
            )}
          />
        )}
        {isUsExclusivelySelected &&
          showProvinceFilter &&
          isProvinceModalOpen && (
            <UsProvinceSelectionModal
              onClose={() => setIsProvinceModalOpen(false)}
              onProvincesSelected={handleProvincesSelected}
              selectedProvinces={segment.locationProvince?.provinces || []}
            />
          )}
      </>
    );
  },
);

export const locationOperatorToText: Record<
  SegmentListSingleItemMembershipOperator,
  string
> = {
  [SegmentListSingleItemMembershipOperator.IN]: "in",
  [SegmentListSingleItemMembershipOperator.NOT_IN]: "not in",
};

export function isUnitedStatesExclusivelySelected(
  countries: CountryCode[],
  operator: SegmentListSingleItemMembershipOperator,
) {
  return (
    countries.length === 1 &&
    countries.includes("US") &&
    operator === SegmentListSingleItemMembershipOperator.IN
  );
}
